import React, { useId, useState } from "react";
import { ArrowsInSimple, ArrowsOutSimple } from "@phosphor-icons/react";

export type SignalTone = "positive" | "watch" | "risk" | "neutral";
export type HealthStatus = "current" | "degraded" | "stale" | "unavailable";

export interface DashboardMetric {
  label: string;
  value: string;
  detail?: string;
}

export interface DashboardTerm {
  term: string;
  plainLanguage: string;
  investorMeaning: string;
}

export interface DashboardNarrative {
  question: string;
  verdict: string;
  summary: string;
  implication: string;
  watch: string;
  statusLabel: string;
  tone: SignalTone;
  confidenceNote?: string;
  terms?: DashboardTerm[];
}

export interface ChartPoint {
  period: string;
  value: number;
  low?: number;
  high?: number;
  detail?: string;
}

export interface BoardChart {
  label: string;
  unit: string;
  /** Human-readable Y-axis definition; unit remains the compact value formatter. */
  axisTitle?: string;
  history: ChartPoint[];
  historyLabel?: string;
  bridgeLabel?: string;
  /** Model estimate that starts the future path; it is not rendered as historical actual. */
  forecastOrigin?: ChartPoint & { label?: string };
  /** Point-only comparison overlays. They never connect to, or alter, the fitted path. */
  markers?: Array<ChartPoint & {
    kind: "current-nowcast" | "external-benchmark";
    label: string;
  }>;
  baseline?: ChartPoint[];
  baselineLabel?: string;
  scenario?: ChartPoint[];
  threshold?: { value: number; label: string };
  explanation?: string;
  intervalLabel?: string;
}

export interface DashboardModel {
  id: string;
  label: string;
  description?: string;
  monitor?: {
    narrative?: DashboardNarrative;
    actual: DashboardMetric;
    asOf: string;
    health: { status: HealthStatus; label: string; detail?: string };
  };
  forecast?: {
    narrative?: DashboardNarrative;
    future: DashboardMetric[];
    interval: { label: string; low: string; high: string };
    backtest: { label: string; value: string; observations: number; comparison?: string };
    scenario: { label: string; active: boolean; impact?: string };
    /** One plain-language sentence of real historical hit-rate evidence. */
    evidence?: string;
    chart?: BoardChart;
    /** Small multiples for targets that must not be blended onto one scale. */
    charts?: BoardChart[];
  };
  /** Always-on plain-language blocks shown beside the chart on the wall. */
  plain?: { method: string; reading: string };
}

function makeFormat(unit: string) {
  const digitsFor = (value: number) => {
    const abs = Math.abs(value);
    if (unit === "×") return 2;
    if (abs >= 100) return 0;
    if (abs >= 2) return 1;
    return 2;
  };
  return (value: number) => {
    const formatted = value.toLocaleString("zh-CN", { maximumFractionDigits: digitsFor(value) });
    if (unit.startsWith("$")) return `$${formatted}${unit.slice(1)}`;
    return `${formatted}${unit}`;
  };
}

function seriesDiffer(a: ChartPoint[] | undefined, b: ChartPoint[] | undefined) {
  if (!a?.length || !b?.length) return false;
  if (a.length !== b.length) return true;
  return a.some((point, index) => Math.abs(point.value - b[index].value) > Math.abs(point.value) * 1e-6 + 1e-9);
}

function ChartExpandButton({ expanded, onToggle, label }: { expanded: boolean; onToggle: () => void; label: string }) {
  const action = expanded ? "退出放大" : "放大图表";
  return (
    <button
      type="button"
      className="chart-expand"
      onClick={onToggle}
      aria-label={`${action}：${label}`}
      aria-pressed={expanded}
      title={action}
      data-chart-expand="true"
    >
      {expanded ? <ArrowsInSimple size={17} aria-hidden="true" /> : <ArrowsOutSimple size={17} aria-hidden="true" />}
    </button>
  );
}

export function MetricChart({ chart, detailed = false }: { chart: BoardChart; detailed?: boolean }) {
  const chartId = useId().replace(/:/g, "");
  const [activePeriod, setActivePeriod] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const width = 720;
  const height = 310;
  const padding = { top: 34, right: 18, bottom: 48, left: 68 };
  const scenario = chart.scenario ?? [];
  const scenarioDiffers = seriesDiffer(scenario, chart.baseline);
  const periods = [...new Set([
    ...chart.history.map((point) => point.period),
    ...(chart.forecastOrigin ? [chart.forecastOrigin.period] : []),
    ...(chart.baseline ?? []).map((point) => point.period),
    ...scenario.map((point) => point.period),
    ...(chart.markers ?? []).map((point) => point.period),
  ])];
  const values = [
    ...chart.history.flatMap((point) => [point.value, point.low, point.high]),
    ...(chart.forecastOrigin ? [chart.forecastOrigin.value, chart.forecastOrigin.low, chart.forecastOrigin.high] : []),
    ...(chart.baseline ?? []).flatMap((point) => [point.value, point.low, point.high]),
    ...scenario.flatMap((point) => [point.value, point.low, point.high]),
    ...(chart.markers ?? []).flatMap((point) => [point.value, point.low, point.high]),
    chart.threshold?.value,
  ].filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  if (periods.length < 2 || values.length < 2) return null;
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const spread = Math.max(rawMax - rawMin, Math.abs(rawMax) * 0.08, 1e-6);
  const min = Math.max(0, rawMin - spread * 0.14);
  const max = rawMax + spread * 0.14;
  const x = (period: string) => padding.left + (periods.indexOf(period) / Math.max(periods.length - 1, 1)) * (width - padding.left - padding.right);
  const y = (value: number) => padding.top + ((max - value) / Math.max(max - min, 1e-9)) * (height - padding.top - padding.bottom);
  const line = (points: ChartPoint[], connect = false) => {
    const connectionPoint = chart.forecastOrigin ?? chart.history.at(-1);
    const pathPoints = connect && connectionPoint ? [connectionPoint, ...points] : points;
    return pathPoints.map((point, index) => `${index ? "L" : "M"}${x(point.period).toFixed(1)},${y(point.value).toFixed(1)}`).join(" ");
  };
  const historyArea = chart.history.length > 1
    ? `${line(chart.history)} L${x(chart.history.at(-1)!.period).toFixed(1)},${(height - padding.bottom).toFixed(1)} L${x(chart.history[0].period).toFixed(1)},${(height - padding.bottom).toFixed(1)} Z`
    : "";
  const bandSource = (scenario.length ? scenario : chart.baseline ?? [])
    .filter((point) => Number.isFinite(point.low) && Number.isFinite(point.high));
  const bandPath = bandSource.length > 1
    ? `${bandSource.map((point, index) => `${index ? "L" : "M"}${x(point.period).toFixed(1)},${y(point.high as number).toFixed(1)}`).join(" ")} ${[...bandSource].reverse().map((point) => `L${x(point.period).toFixed(1)},${y(point.low as number).toFixed(1)}`).join(" ")} Z`
    : "";
  const format = makeFormat(chart.unit);
  const futureStart = chart.forecastOrigin?.period ?? chart.baseline?.[0]?.period ?? scenario[0]?.period;
  const lastActual = chart.history.at(-1);
  const forecastBridge = lastActual && chart.forecastOrigin && lastActual.period !== chart.forecastOrigin.period
    ? [lastActual, chart.forecastOrigin]
    : [];
  const terminalPoint = (scenarioDiffers ? scenario.at(-1) : undefined) ?? chart.baseline?.at(-1) ?? scenario.at(-1);
  const singleForecastHoverSource = scenario.length ? scenario : chart.baseline ?? [];
  const hoverPoints = [
    ...chart.history.map((point) => ({ ...point, series: chart.historyLabel ?? "历史实际", kind: "actual" as const })),
    ...(chart.forecastOrigin ? [{
      ...chart.forecastOrigin,
      series: chart.forecastOrigin.label ?? "最新完整月模型估计",
      kind: "forecast-origin" as const,
    }] : []),
    ...(scenarioDiffers ? chart.baseline ?? [] : singleForecastHoverSource)
      .map((point) => ({ ...point, series: scenarioDiffers ? "基准路径" : chart.baselineLabel ?? "预测路径", kind: "baseline" as const })),
    ...(scenarioDiffers ? scenario.map((point) => ({ ...point, series: "当前假设", kind: "scenario" as const })) : []),
    ...(chart.markers ?? []).map((point) => ({ ...point, series: point.label })),
  ];
  const activeRows = activePeriod ? hoverPoints.filter((point) => point.period === activePeriod) : [];
  const readoutPeriod = activePeriod ?? terminalPoint?.period ?? lastActual?.period ?? periods.at(-1)!;
  const readoutRows = hoverPoints.filter((point) => point.period === readoutPeriod);
  const activeX = activePeriod ? x(activePeriod) : null;
  const activeXPercent = activeX == null ? 0 : activeX / width * 100;
  const xTickCount = Math.min(periods.length, detailed ? 6 : 4);
  const xTickIndexes = [...new Set(Array.from({ length: xTickCount }, (_, index) =>
    Math.round(index * (periods.length - 1) / Math.max(xTickCount - 1, 1))))];
  const interactionZones = periods.map((period, index) => {
    const pointX = x(period);
    const previousX = index > 0 ? x(periods[index - 1]) : padding.left;
    const nextX = index < periods.length - 1 ? x(periods[index + 1]) : width - padding.right;
    const left = index === 0 ? padding.left : (previousX + pointX) / 2;
    const right = index === periods.length - 1 ? width - padding.right : (pointX + nextX) / 2;
    return { period, left, width: Math.max(right - left, 1) };
  });
  const description = chart.baseline?.length || scenario.length
    ? `历史实际与预测路径。预测终点 ${terminalPoint ? format(terminalPoint.value) : "未提供"}，阴影区间 ${bandSource.at(-1)?.low != null ? format(bandSource.at(-1)!.low!) : "未提供"} 至 ${bandSource.at(-1)?.high != null ? format(bandSource.at(-1)!.high!) : "未提供"}。`
    : `历史实际趋势，共 ${chart.history.length} 个观测。`;

  return (
    <figure
      className={`mchart${detailed ? " mchart--detailed" : ""}${expanded ? " chart-is-expanded" : ""}`}
      data-chart-interactive="true"
      role={expanded ? "dialog" : undefined}
      aria-modal={expanded ? "true" : undefined}
      aria-label={expanded ? `${chart.label}放大图表` : undefined}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse") setActivePeriod(null);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") setExpanded(false);
      }}
    >
      <ChartExpandButton expanded={expanded} onToggle={() => setExpanded((value) => !value)} label={chart.label} />
      <strong className="chart-expanded-title">{chart.label}</strong>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={chart.label}>
        <title>{chart.label}</title>
        <desc>{description}</desc>
        <defs>
          <linearGradient id={`${chartId}-actual-fill`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.10" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        {futureStart ? (
          <rect
            x={x(futureStart)}
            y={padding.top}
            width={Math.max(width - padding.right - x(futureStart), 0)}
            height={height - padding.top - padding.bottom}
            className="mchart__future-zone"
          />
        ) : null}
        <text x={padding.left} y={16} textAnchor="start" className="mchart__axis-title">Y · {chart.axisTitle ?? (chart.unit || "数值")}</text>
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const value = min + (max - min) * ratio;
          const lineY = y(value);
          return (
            <g key={ratio}>
              <line x1={padding.left} x2={width - padding.right} y1={lineY} y2={lineY} className="mchart__grid" />
              <text x={padding.left - 8} y={lineY + 4} textAnchor="end" className="mchart__axis-label">{format(value)}</text>
            </g>
          );
        })}
        <line x1={padding.left} x2={padding.left} y1={padding.top} y2={height - padding.bottom} className="mchart__axis" />
        <line x1={padding.left} x2={width - padding.right} y1={height - padding.bottom} y2={height - padding.bottom} className="mchart__axis" />
        {chart.threshold ? (
          <g className="mchart__threshold">
            <line x1={padding.left} x2={width - padding.right} y1={y(chart.threshold.value)} y2={y(chart.threshold.value)} />
            {/* Anchor left: terminal value labels own the right edge. */}
            <text x={padding.left + 6} y={y(chart.threshold.value) - 7} textAnchor="start">{chart.threshold.label}</text>
          </g>
        ) : null}
        {futureStart ? <line x1={x(futureStart)} x2={x(futureStart)} y1={padding.top} y2={height - padding.bottom} className="mchart__divider" /> : null}
        {historyArea ? <path d={historyArea} fill={`url(#${chartId}-actual-fill)`} className="mchart__area" /> : null}
        {bandPath ? <path d={bandPath} className="mchart__band" /> : null}
        <path d={line(chart.history)} className="mchart__line mchart__line--actual" />
        {forecastBridge.length ? <path d={line(forecastBridge)} className="mchart__line mchart__line--bridge" /> : null}
        {chart.baseline?.length ? <path d={line(chart.baseline, true)} className={`mchart__line mchart__line--baseline${scenarioDiffers ? "" : " mchart__line--primary"}`} /> : null}
        {scenarioDiffers ? <path d={line(scenario, true)} className="mchart__line mchart__line--scenario" /> : null}
        {chart.forecastOrigin ? (
          <circle
            cx={x(chart.forecastOrigin.period)}
            cy={y(chart.forecastOrigin.value)}
            r="4.4"
            className="mchart__forecast-origin"
          />
        ) : null}
        {(scenarioDiffers ? scenario : chart.baseline ?? scenario).map((point) => (
          <circle key={point.period} cx={x(point.period)} cy={y(point.value)} r="3.2" className={`mchart__dot${scenarioDiffers ? " mchart__dot--scenario" : ""}`} />
        ))}
        {lastActual ? <circle cx={x(lastActual.period)} cy={y(lastActual.value)} r="3.6" className="mchart__dot mchart__dot--actual" /> : null}
        {(chart.markers ?? []).map((point) => point.kind === "external-benchmark" ? (
          <g key={`${point.kind}-${point.period}-${point.value}`} className="mchart__marker mchart__marker--external">
            <line x1={x(point.period) - 5} x2={x(point.period) + 5} y1={y(point.value) - 5} y2={y(point.value) + 5} />
            <line x1={x(point.period) - 5} x2={x(point.period) + 5} y1={y(point.value) + 5} y2={y(point.value) - 5} />
          </g>
        ) : (
          <circle
            key={`${point.kind}-${point.period}-${point.value}`}
            cx={x(point.period)}
            cy={y(point.value)}
            r="4.2"
            className="mchart__marker mchart__marker--current"
          />
        ))}
        {lastActual ? (
          <text
            x={x(lastActual.period)}
            y={Math.max(y(lastActual.value) - 11, 12)}
            textAnchor={futureStart ? "end" : "middle"}
            className="mchart__value-label mchart__value-label--actual"
          >
            {format(lastActual.value)}
          </text>
        ) : null}
        {terminalPoint ? (
          <text
            x={x(terminalPoint.period)}
            y={Math.max(y(terminalPoint.value) - 11, 12)}
            textAnchor="end"
            className="mchart__value-label mchart__value-label--forecast"
          >
            {format(terminalPoint.value)}
          </text>
        ) : null}
        {xTickIndexes.map((index) => (
          <g key={periods[index]} className="mchart__x-tick">
            <line x1={x(periods[index])} x2={x(periods[index])} y1={height - padding.bottom} y2={height - padding.bottom + 5} />
            <text
              x={x(periods[index])}
              y={height - 25}
              textAnchor={index === 0 ? "start" : index === periods.length - 1 ? "end" : "middle"}
              className="mchart__axis-label"
            >
              {periods[index]}
            </text>
          </g>
        ))}
        <text x={width - padding.right} y={height - 7} textAnchor="end" className="mchart__axis-title">X · 时间</text>
        {futureStart ? <text x={x(futureStart) + 5} y={padding.top + 13} textAnchor="start" className="mchart__forecast-label">预测起点</text> : null}
        {interactionZones.map((zone) => {
          const rows = hoverPoints.filter((point) => point.period === zone.period);
          const label = rows.map((point) => [
            `${point.series} ${format(point.value)}`,
            point.low != null && point.high != null ? `范围 ${format(point.low)} 至 ${format(point.high)}` : "",
            point.detail ?? "",
          ].filter(Boolean).join("，")).join("；");
          return (
            <rect
              key={zone.period}
              x={zone.left}
              y={padding.top}
              width={zone.width}
              height={height - padding.top - padding.bottom}
              className="mchart__interaction-zone"
              role="button"
              tabIndex={0}
              aria-label={`${zone.period}：${label}`}
              onPointerEnter={(event) => {
                if (event.pointerType === "mouse") setActivePeriod(zone.period);
              }}
              onPointerDown={(event) => {
                setActivePeriod((current) => event.pointerType !== "mouse" && current === zone.period ? null : zone.period);
              }}
              onFocus={() => setActivePeriod(zone.period)}
              onBlur={() => setActivePeriod(null)}
            >
              <title>{`${zone.period}：${label}`}</title>
            </rect>
          );
        })}
        {activeX != null ? <line x1={activeX} x2={activeX} y1={padding.top} y2={height - padding.bottom} className="mchart__crosshair" /> : null}
        {activeRows.map((point, index) => (
          <circle
            key={`${point.kind}-${point.period}-active-${index}`}
            cx={x(point.period)}
            cy={y(point.value)}
            r="5"
            className={`mchart__active-dot mchart__active-dot--${point.kind}`}
          />
        ))}
      </svg>
      {activePeriod && activeRows.length ? (
        <div
          className={`mchart__tooltip${activeXPercent > 68 ? " mchart__tooltip--left" : activeXPercent < 32 ? " mchart__tooltip--right" : ""}`}
          style={{ left: `${activeXPercent}%` }}
          role="status"
          aria-live="polite"
        >
          <strong>{activePeriod}</strong>
          {activeRows.map((point, index) => (
            <div key={`${point.kind}-${point.period}-tooltip-${index}`}>
              <span className={`mchart__tooltip-swatch mchart__tooltip-swatch--${point.kind}`} aria-hidden="true" />
              <em>{point.series}</em>
              <b>{format(point.value)}</b>
              {point.low != null && point.high != null ? <small>范围 {format(point.low)} — {format(point.high)}</small> : null}
              {point.detail ? <small>{point.detail}</small> : null}
            </div>
          ))}
        </div>
      ) : null}
      {readoutRows.length ? (
        <div className="chart-touch-readout" role="status" aria-live="polite" data-chart-touch-readout="true">
          <header>
            <span>当前注释</span>
            <strong>{readoutPeriod}</strong>
          </header>
          <div className="chart-touch-readout__rows">
            {readoutRows.map((point, index) => (
              <div key={`${point.kind}-${point.period}-touch-${index}`}>
                <span className={`mchart__tooltip-swatch mchart__tooltip-swatch--${point.kind}`} aria-hidden="true" />
                <em>{point.series}</em>
                <b>{format(point.value)}</b>
                {point.low != null && point.high != null ? <small>区间 {format(point.low)} — {format(point.high)}</small> : null}
                {point.detail ? <small>{point.detail}</small> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <ul className="sr-only" aria-label={`${chart.label}逐期数据`}>
        {hoverPoints.map((point, index) => (
          <li key={`${point.series}-${point.period}-a11y-${index}`}>{[
            `${point.series} · ${point.period}: ${format(point.value)}`,
            point.low != null && point.high != null ? `范围 ${format(point.low)} 至 ${format(point.high)}` : "",
            point.detail ?? "",
          ].filter(Boolean).join(" · ")}</li>
        ))}
      </ul>
      <figcaption>
        <span><i className="swatch swatch--actual" />{chart.historyLabel ?? "历史实际"}</span>
        {forecastBridge.length ? <span><i className="swatch swatch--bridge" />{chart.bridgeLabel ?? "公开锚点 → 当前估计"}</span> : null}
        {chart.forecastOrigin ? <span><i className="swatch swatch--forecast-origin" />{chart.forecastOrigin.label ?? "最新完整月模型估计"}</span> : null}
        {chart.baseline?.length ? <span><i className="swatch swatch--baseline" />{scenarioDiffers ? "基准路径" : chart.baselineLabel ?? "预测路径"}</span> : null}
        {scenarioDiffers ? <span><i className="swatch swatch--scenario" />当前假设</span> : null}
        {chart.markers?.some((point) => point.kind === "current-nowcast") ? <span><i className="swatch swatch--current-nowcast" />{chart.markers.find((point) => point.kind === "current-nowcast")!.label}</span> : null}
        {chart.markers?.some((point) => point.kind === "external-benchmark") ? <span><i className="swatch swatch--external-benchmark" />外部模型参照</span> : null}
        {bandPath ? <span><i className="swatch swatch--band" />{chart.intervalLabel ?? "预测误差范围"}</span> : null}
        {chart.threshold ? <span><i className="swatch swatch--threshold" />{chart.threshold.label}</span> : null}
        <span className="mchart__interaction-hint interaction-hint--pointer">悬停、点击或键盘聚焦查看逐期明细</span>
        <span className="mchart__interaction-hint interaction-hint--touch">点击图表切换时期；明细显示在图表下方</span>
      </figcaption>
    </figure>
  );
}

export function InvestorGlossary({ terms }: { terms?: DashboardTerm[] }) {
  if (!terms?.length) return null;
  return (
    <details className="glossary">
      <summary>这些数字是什么意思？</summary>
      <dl>
        {terms.map((item) => (
          <div key={item.term}>
            <dt>{item.term}</dt>
            <dd>{item.plainLanguage}<small>{item.investorMeaning}</small></dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

function WallCard({
  model,
  selected,
  onOpen,
}: {
  model: DashboardModel;
  selected: boolean;
  onOpen: (id: string) => void;
}) {
  const headingId = useId().replace(/:/g, "");
  const forecast = model.forecast;
  const narrative = forecast?.narrative;
  const charts = forecast?.charts?.length ? forecast.charts : forecast?.chart ? [forecast.chart] : [];
  if (!charts.length || !narrative) {
    return (
      <article className="wall-card wall-card--unavailable" role="status">
        <header className="wall-card__bar">
          <h2>{model.description ?? model.label}</h2>
          <span className="tone-chip tone-chip--neutral">暂不可用</span>
        </header>
        <div className="wall-card__body">
          <p className="wall-card__verdict">{model.monitor?.actual.detail ?? "该模型输入尚未发布，恢复后自动出现。"}</p>
        </div>
      </article>
    );
  }
  return (
    <article className={`wall-card wall-card--${model.id}${selected ? " wall-card--selected" : ""}`} aria-labelledby={headingId}>
      <header className="wall-card__bar">
        <h2 id={headingId}>{narrative.question}</h2>
        <span className={`tone-chip tone-chip--${narrative.tone}`}>{narrative.statusLabel}</span>
      </header>
      <div className="wall-card__body">
        <p className="wall-card__verdict">{narrative.verdict}</p>
        <dl className="wall-card__numbers">
          {model.monitor ? (
            <div>
              <dt>{model.monitor.actual.label}</dt>
              <dd>{model.monitor.actual.value}</dd>
              {model.monitor.actual.detail ? <small className="wall-card__current-split">{model.monitor.actual.detail}</small> : null}
            </div>
          ) : null}
          {forecast.future.slice(0, 2).map((item) => (
            <div key={item.label}>
              <dt>{item.label}</dt>
              <dd>{item.value}</dd>
              {item.detail ? <small>{item.detail}</small> : null}
            </div>
          ))}
        </dl>
        <div className={`wall-card__chart-well${charts.length > 1 ? " wall-card__chart-well--split" : ""}`}>
          {charts.map((chart) => (
            <section className="wall-card__chart" key={chart.label} aria-label={chart.label}>
              {charts.length > 1 ? <h3>{chart.label}</h3> : null}
              <MetricChart chart={chart} />
            </section>
          ))}
        </div>
        {model.plain ? (
          <div className="wall-card__explain">
            <div>
              <h3>我们在算什么</h3>
              <p>{model.plain.method}</p>
            </div>
            <div>
              <h3>怎么读这张图</h3>
              <p>{model.plain.reading}</p>
            </div>
          </div>
        ) : null}
        <div className="wall-card__rule" aria-hidden="true" />
        <footer className="wall-card__foot">
          <span className="wall-card__evidence">{forecast.evidence ?? `${forecast.backtest.observations} 个历史样本外观测`}</span>
          <span className="wall-card__asof">{model.monitor ? `数据截至 ${model.monitor.asOf}` : ""}</span>
          <button type="button" className="wall-card__open" onClick={() => onOpen(model.id)} aria-pressed={selected}>
            展开详情与压力测试
          </button>
        </footer>
      </div>
    </article>
  );
}

export function ChartWall({
  models,
  selectedModelId,
  onOpen,
}: {
  models: readonly DashboardModel[];
  selectedModelId: string | null;
  onOpen: (id: string) => void;
}) {
  return (
    <section className="wall" aria-label="七个投资问题总览">
      {models.map((model) => (
        <WallCard key={model.id} model={model} selected={model.id === selectedModelId} onOpen={onOpen} />
      ))}
    </section>
  );
}
